import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, registerables } from 'chart.js';
import { SimulationService, KPIData } from '../shared/services/simulation.service';
import { ConvertService } from "../shared/services/convert.service";

Chart.register(...registerables);

@Component({
  selector: 'app-kpi-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './kpi-popup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiPopupComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();

  @ViewChild('pieExtCanvas') private pieExtCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('pieErrCanvas') private pieErrCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('lineCanvas') private lineCanvas!: ElementRef<HTMLCanvasElement>;

  private simulationService = inject(SimulationService);
  private convertService = inject(ConvertService);

  loading = signal(true);
  refreshing = signal(false);
  data = signal<KPIData | null>(null);
  err = signal(false);

  private cdRef = inject(ChangeDetectorRef);

  private pieExtChart: Chart | null = null;
  private pieErrChart: Chart | null = null;
  private lineChart: Chart | null = null;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.pieExtChart?.destroy();
    this.pieErrChart?.destroy();
    this.lineChart?.destroy();
  }

  async load(): Promise<void> {
    if (this.data()) {
      this.refreshing.set(true);
    } else {
      this.loading.set(true);
    }
    this.err.set(false);
    try {
      this.data.set(await this.simulationService.getKPIData());
    } catch (e) {
      console.error('[KPI] getKPIData error:', e);
      this.err.set(true);
    } finally {
      this.loading.set(false);
      this.refreshing.set(false);
    }
    if (this.data()) {
      this.cdRef.detectChanges(); // flush @else block so canvas elements exist in the DOM
      this.initCharts();
    }
  }

  private initCharts(): void {
    const d = this.data();
    if (!d) return;
    this.initPieExt(d);
    this.initPieErr(d);
    this.initLine(d);
  }

  private initPieExt(d: KPIData): void {
    if (!this.pieExtCanvas) return;
    const items = d.byDescription;
    const chartData = {
      labels: items.map(i => i.label),
      datasets: [{
        data: items.map(i => i.count),
        backgroundColor: items.map(i => i.color),
        borderColor: 'white',
        borderWidth: 2
      }],
    };
    if (this.pieExtChart) {
      this.pieExtChart.data = chartData;
      this.pieExtChart.update();
    } else {
      this.pieExtChart = new Chart(this.pieExtCanvas.nativeElement, {
        type: 'pie',
        data: chartData,
        options: {
          responsive: true,
          plugins: {
            legend: { position: 'right' },
            tooltip: { callbacks: { label: ctx => this.sliceLabel(ctx) } },
          },
        },
      });
    }
  }

  private initPieErr(d: KPIData): void {
    if (!this.pieErrCanvas) return;
    const items = d.errorDistribution;
    const chartData = {
      labels: items.map(i => this.convertService.CalErrorTexts[i.label] ?? i.label),
      datasets: [{
        data: items.map(i => i.count),
        backgroundColor: items.map(i => i.color),
        borderColor: 'white',
        borderWidth: 2
      }],
    };
    if (this.pieErrChart) {
      this.pieErrChart.data = chartData;
      this.pieErrChart.update();
    } else {
      this.pieErrChart = new Chart(this.pieErrCanvas.nativeElement, {
        type: 'pie',
        data: chartData,
        options: {
          responsive: true,
          plugins: {
            legend: { position: 'right' },
            tooltip: { callbacks: { label: ctx => this.sliceLabel(ctx) } },
          },
        },
      });
    }
  }

  private initLine(d: KPIData): void {
    if (!this.lineCanvas) return;
    const chartData = {
      labels: d.dailyData.map(p => p.date),
      datasets: [{
        data: d.dailyData.map(p => p.count),
        borderColor: '#0ab2d7',
        backgroundColor: 'rgba(10,178,215,0.10)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      }],
    };
    if (this.lineChart) {
      this.lineChart.data = chartData;
      this.lineChart.update();
    } else {
      this.lineChart = new Chart(this.lineCanvas.nativeElement, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          scales: {
            x: {
              ticks: {
                maxTicksLimit: 7,
                callback: (_val, idx) => {
                  const date = d.dailyData[idx]?.date;
                  return date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';
                },
              },
            },
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
          plugins: { legend: { display: false } },
        },
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sliceLabel(ctx: any): string {
    const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
    const pct = Math.round((ctx.parsed / total) * 100);
    return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
  }
}
